/** @jsxImportSource @emotion/react */

import * as React from 'react'
import styled from '@emotion/styled'
import { css, jsx } from '@emotion/react'
import { useFocus } from '../../hooks/focus'
import { generateHash } from '../../utils/random'
import { Primary } from '@sphinx/components-styles'

type RadioProps = React.InputHTMLAttributes<HTMLInputElement>

export const Radio: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({
  checked,
  disabled,
  children,
  ...props
}) => {
  const id = props.id || generateHash()
  const { focused, handleClick, handleMouseUp } = useFocus(props)

  return (
    <RadioWithLabel htmlFor={id} onMouseUp={handleMouseUp}>
      <RadioControl
        id={id}
        {...props}
        type="radio"
        focused={focused}
        checked={checked}
        disabled={disabled}
        onClick={handleClick}
      />
      <StyledRadio checked={checked} disabled={disabled} />
      <Label
        htmlFor={id}
        css={css`
          cursor: ${disabled ? 'initial' : 'pointer'};
          color: ${disabled ? Primary.navy[20] : Primary.navy[100]};
        `}
      >
        {children}
      </Label>
    </RadioWithLabel>
  )
}

const Label = styled.label`
  font-size: 12px;
  margin-left: 8px;
  line-height: 24px;
  font-style: normal;
  font-family: Roboto;
  font-weight: normal;
`

const innerCircleStyles = css`
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 50%;
  position: absolute;
  top: calc(50% - 5px);
  left: calc(50% - 5px);
`

const RadioControl = styled.input<any>`
  ${({ disabled, focused }) =>
    focused &&
    !disabled &&
    css`
      &:focus {
        outline: none;

        + div {
          outline-color: ${Primary.orange[100]};
          outline-offset: 2px;
          outline-style: solid;
        }
      }
    `}

  &:checked {
    & + div {
      border: 1px solid ${Primary.orange[100]};

      &::after {
        ${innerCircleStyles}
        background: ${Primary.orange[100]};
      }

      &:hover {
        border: 1px solid ${Primary.orange[80]};

        &::after {
          background: ${Primary.orange[80]};
        }
      }
    }

    &:disabled {
      & + div {
        &::after {
          ${innerCircleStyles}
          background: ${Primary.navy[20]};
        }

        &,
        &:hover {
          border: 1px solid ${Primary.navy[20]};
        }
      }
    }
  }

  &:disabled + div {
    &,
    &:hover {
      cursor: initial;
      background: ${Primary.neutral.white};
      border: 1px solid ${Primary.navy[20]};
    }
  }
`

const RadioWithLabel = styled.label`
  display: flex;
  padding: 6px 8px;
  width: max-content;
  align-items: center;
  flex-flow: row nowrap;
`

const StyledRadio = styled.div<RadioProps>`
  width: 18px;
  height: 18px;
  outline: none;
  margin-left: -18px;
  position: relative;
  border-radius: 50%;
  box-sizing: border-box;
  border: 1px solid ${Primary.navy[40]};
  background: ${Primary.neutral.white};

  &:hover {
    cursor: pointer;
    border: 1px solid ${Primary.navy[100]};

    &::after {
      background: ${Primary.orange[80]};
    }
  }
`
